import { AsyncOperationState } from '@mimir/common/dto/asyncOperationPrimitives';
import { CommonProgressStatus } from '@mimir/common/dto/itemModelPrimitives';
import { isDeleting, type StandardItemEntryDto } from '@mimir/common/dto/itemModels';
import { ItemState, type ItemStateType } from '@mimir/common/dto/itemState';
import type { ProgressInfo } from '@mimir/common/dto/progress';
import type { Clock } from '@mimir/common/types/clock';
import { isAbortError } from '@mimir/common/util/abortControl';
import { asyncSleep } from '@mimir/common/util/asyncSleep';
import type { CompleteType } from '@mimir/common/util/general';


// Handles the periodic polling of search hits, so that we auto update
// the UI with upload progress, live media duration, etc.
export class SearchHitsPoller {

  private isLooping = false;
  private skipNextSleep: boolean = false;
  private sleepAbort: AbortController | null = null;

  constructor(
    private pollFunc: () => Promise<void>,
    private clock: Clock,
  ) {
  }

  // Starts the loop that polls relevant search hits every few seconds
  loop(): void {
    void this.runLoop().catch(err => {
      console.error('Error in search hits poller loop:', err);
    });
  }

  // Makes sure a new poll is initiated immediately, or after
  // the current poll finishes if one is in-flight.
  pollNow(): void {
    if (!this.isLooping) {
      this.loop();
      return;
    }

    if (this.sleepAbort) {
      // If we are sleeping, aborting the sleep will init a new poll
      this.sleepAbort.abort();
    } else {
      // Otherwise there is an ongoing poll that we let finish, and
      // then poll again immediately after
      this.skipNextSleep = true;
    }
  }

  // Stops the polling loop (but without aborting any in-flight poll).
  stop(): void {
    this.isLooping = false;
    this.sleepAbort?.abort();
  }

  private async runLoop() {
    if (this.isLooping) {
      return;
    }
    this.isLooping = true;
    try {
      while (this.isLooping) {
        const start = this.clock.monotonicNow();
        await this.pollFunc();
        if (!this.isLooping) {
          break;
        }
        await this.sleepAfterPoll(this.clock.monotonicNow() - start);
      }
    } finally {
      this.isLooping = false;
    }
  }

  private async sleepAfterPoll(pollTime: number) {
    if (this.skipNextSleep) {
      this.skipNextSleep = false;
      return;
    }
    const minimumSleepMs = 5000;
    const cooldown = Math.max(minimumSleepMs, pollTime * 4); // Make sure the poll consumes at most a fixed fraction of the loop time
    this.sleepAbort = new AbortController();
    try {
      await asyncSleep(this.clock, cooldown, this.sleepAbort.signal);
    } catch (e) {
      if (this.sleepAbort.signal.aborted || isAbortError(e)) {
        return;
      }
      throw e;
    } finally {
      this.sleepAbort = null;
    }
  }
}


export async function doItemsPoll(
  getItems: (ids: string[]) => Promise<StandardItemEntryDto[]>,
  currentPollListIds: () => string[],
) {
  const requestItemIds = currentPollListIds();
  if (requestItemIds.length === 0) {
    return {
      returnedItems: [],
      updatedPollList: [],
    };
  }

  const returnedItems: StandardItemEntryDto[] = await getItems(requestItemIds);

  // As we poll, some items may change state and no longer require polling,
  // so let's remove from the list any item that should not be polled anymore.
  //
  // To do that, let's get a fresh list of IDs in case the list has changed while we
  // were awaiting getItemsWithDefaultFields()
  const updatedPollList = new Set<string>(currentPollListIds());
  returnedItems.forEach(item => {
    if (!shouldPollForUpdates(toPollItem(item))) {
      updatedPollList.delete(item.id);
    }
  });
  // If an item in requestItemIds was not returned, then it either no longer exists
  // or the user does not have access to it, and we should remove it from the poll list.
  const returnedItemIds = new Set(returnedItems.map(item => item.id));
  const notFoundIds = requestItemIds.filter(id => !returnedItemIds.has(id));
  for (const id of notFoundIds) {
    updatedPollList.delete(id);
  }

  return {
    returnedItems,
    updatedPollList: [...updatedPollList],
  };
}


export function toPollItem(item: StandardItemEntryDto): CompleteType<PollCheckItem> {
  return {
    progressInfo: item.progressInfo,
    itemState: item.itemState,
    // `media.streaming` covers Mimir-hosted live items; a TAMS-referenced live
    // item has no `media`, so honor the persisted `isLive` flag too. This keeps
    // a live TAMS hit in the poll list so it auto-refreshes when its flow
    // finalizes (the checker clears isLive and stamps the duration).
    isLive: item.isLive === true || item.media?.streaming === true,
    transcodingState: item.transcodingState ?? undefined,
    isDeleting: isDeleting(item),
  };
}


export function shouldPollForUpdates(hit: PollCheckItem) {
  return hit.isLive || (
    !hit.isDeleting &&
    hit.progressInfo !== undefined &&
    hit.progressInfo.status !== 'NEW' &&
    hit.itemState !== ItemState.COMPLETE &&
    hit.itemState !== ItemState.ERROR &&
    hit.transcodingState !== AsyncOperationState.COMPLETE
    && hit.progressInfo.status !== CommonProgressStatus.COMPLETE
  );
}

/**
 * Contains the fields checked by shouldPollForUpdates
 */
export interface PollCheckItem {
  progressInfo: ProgressInfo | undefined;
  itemState: ItemStateType;
  isLive: boolean;
  transcodingState: AsyncOperationState | undefined;
  isDeleting: boolean;
}
